import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { STORAGE_KEYS } from '@/constants/storage';
import { env } from '@/config/env';
import { useAppSelector } from '@/hooks/useAppSelector';
import { realtimeClient } from './RealtimeClient';
import { realtimeChannels } from './channels';
import { invalidateOnEvent } from './invalidateOnEvent';
import type { RealtimeConnectionStatus, RealtimeMessage } from './types';

type RealtimeContextValue = {
  status: RealtimeConnectionStatus;
  subscribe: (destination: string, handler?: (msg: RealtimeMessage) => void) => () => void;
};

export const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const user = useAppSelector((s) => s.auth.user);
  const orgId = user?.organizationId ?? undefined;
  const token = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) : null;
  const [status, setStatus] = useState<RealtimeConnectionStatus>('idle');

  useEffect(() => {
    if (!token) {
      realtimeClient.disconnect();
      setStatus('idle');
      return;
    }
    if (!env.realtimeEnabled) {
      return;
    }
    realtimeClient.setStatusListener(setStatus);
    let cancelled = false;
    void realtimeClient.connect(token).then(() => {
      if (!cancelled) {
        setStatus(realtimeClient.connectionStatus);
      }
    });
    return () => {
      cancelled = true;
      realtimeClient.setStatusListener(null);
      realtimeClient.disconnect();
    };
  }, [token]);

  useEffect(() => {
    if (!token || !orgId || status !== 'connected') return;
    const unsubs = [
      realtimeClient.subscribe(realtimeChannels.userNotifications, (msg) => {
        invalidateOnEvent(qc, msg);
      }),
      realtimeClient.subscribe(realtimeChannels.userApprovals, (msg) => {
        invalidateOnEvent(qc, msg);
      }),
      realtimeClient.subscribe(realtimeChannels.orgDashboard(orgId), (msg) => {
        invalidateOnEvent(qc, msg);
      }),
    ];
    return () => unsubs.forEach((u) => u());
  }, [token, orgId, status, qc]);

  const subscribe = useCallback(
    (destination: string, handler?: (msg: RealtimeMessage) => void) =>
      realtimeClient.subscribe(destination, (msg) => {
        handler?.(msg);
        invalidateOnEvent(qc, msg);
      }),
    [qc],
  );

  const value = useMemo(
    () => ({ status, subscribe }),
    [status, subscribe],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) {
    throw new Error('useRealtime must be used within RealtimeProvider');
  }
  return ctx;
}

export function useRealtimeStatus(): RealtimeConnectionStatus {
  return useContext(RealtimeContext)?.status ?? 'idle';
}
